import { CheckCircle, Clock, Wrench, MapPin } from 'lucide-react';
import type { Job } from '../../types/jobs';

interface JobCompletionSummaryProps {
  job: Job;
}

function JobCompletionSummary({ job }: JobCompletionSummaryProps) {
  const totalMinutes = job.actual_start && job.actual_end
    ? Math.round((new Date(job.actual_end).getTime() - new Date(job.actual_start).getTime()) / 60000)
    : null;

  const hours = totalMinutes ? Math.floor(totalMinutes / 60) : 0;
  const minutes = totalMinutes ? totalMinutes % 60 : 0;

  const primaryEquip = job.equipment.find(e => e.role === 'primary');
  const implements_ = job.equipment.filter(e => e.role === 'implement');

  return (
    <div className="bg-success-50 border border-success-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle className="h-5 w-5 text-success-600" />
        <span className="font-semibold text-success-800">Job Completed</span>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        {/* Duration */}
        {totalMinutes != null && (
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-neutral-500" />
            <div>
              <p className="text-neutral-500 text-xs">Total Time</p>
              <p className="font-medium text-neutral-800">{hours > 0 ? `${hours}h ` : ''}{minutes}m</p>
            </div>
          </div>
        )}

        {/* Coverage */}
        {job.coverage_pct != null && (
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-neutral-500" />
            <div>
              <p className="text-neutral-500 text-xs">Coverage</p>
              <p className="font-medium text-neutral-800">{Number(job.coverage_pct).toFixed(1)}%</p>
            </div>
          </div>
        )}

        {/* Equipment hours */}
        {primaryEquip && (
          <div className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-neutral-500" />
            <div>
              <p className="text-neutral-500 text-xs">{primaryEquip.name}</p>
              <p className="font-medium text-neutral-800">{primaryEquip.current_hours ?? '—'}h</p>
            </div>
          </div>
        )}

        {implements_.map(impl => (
          <div key={impl.equipment_id} className="flex items-center gap-2">
            <Wrench className="h-4 w-4 text-neutral-500" />
            <div>
              <p className="text-neutral-500 text-xs">{impl.name}</p>
              <p className="font-medium text-neutral-800">{impl.current_hours ?? '—'}h</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default JobCompletionSummary;
